import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Badge from './Badge.jsx';
import MODULE_BADGES from '../lib/moduleBadges.js';

// Simplified subscription editor for SALES LEADS.
//
// Customers see the full grid (renewal date, % increase, next price).
// Leads don't have renewal dates — a prospect's package is just a set
// of modules and their proposed annual prices. So the lead view shows
// two columns per row: the module (with coloured badge) and an editable
// annual price. A module is included in the quote iff a price is
// entered — blank price means "not part of the package."
//
// Storage shares the existing `subscriptions` table — the server's
// upsert was relaxed to accept lead contact_ids. We clear renewal_date
// on save (leads carry none); price is the activeness signal here and
// quoteLineItems includes any priced module in the lead's quote.

// All 16 subscription modules. The lead view exposes annual price only;
// renewal_date / pct_increase / next_price stay null until conversion.
const MODULES = [
  { key: 'live_transactions', label: 'Real Time Inventory', priceField: 'live_transactions_annual', renewalField: 'live_transactions_renewal_date' },
  { key: 'price_book',        label: 'Price Book',          priceField: 'price_book_annual',        renewalField: 'price_book_renewal_date'        },
  { key: 'fuel_manager',      label: 'Fuel Manager',        priceField: 'fuel_manager_annual',      renewalField: 'fuel_manager_renewal_date'      },
  { key: 'charge',            label: 'Charge Accounts',     priceField: 'charge_annual',            renewalField: 'charge_renewal_date'            },
  { key: 'tx_exempt',         label: 'Tax Exempt',          priceField: 'tx_exempt_annual',         renewalField: 'tx_exempt_renewal_date'         },
  { key: 'car_wash',          label: 'Car Wash',            priceField: 'car_wash_annual',          renewalField: 'car_wash_renewal_date'          },
  { key: 'adv_report',        label: 'Advanced Reporting',  priceField: 'adv_report_annual',        renewalField: 'adv_report_renewal_date'        },
  { key: 'fuel_price',        label: 'Fuel Price Changes',  priceField: 'fuel_price_annual',        renewalField: 'fuel_price_renewal_date'        },
  { key: 'case_break',        label: 'Case Breakdown',      priceField: 'case_break_annual',        renewalField: 'case_break_renewal_date'        },
  { key: 'dealer_comm',       label: 'Dealer Commission',   priceField: 'dealer_comm_annual',       renewalField: 'dealer_comm_renewal_date'       },
  { key: 'time_rest',         label: 'Time Restrictions',   priceField: 'time_rest_annual',         renewalField: 'time_rest_renewal_date'         },
  { key: 'sft_file',          label: 'SFT File',            priceField: 'sft_file_annual',          renewalField: 'sft_file_renewal_date'          },
  { key: 'private_label',     label: 'Private Label',       priceField: 'private_label_annual',     renewalField: 'private_label_renewal_date'     },
  // ── Mission Control alignment additions ────────────────────────
  { key: 'imperial_import',   label: 'Imperial Import',     priceField: 'imperial_import_annual',   renewalField: 'imperial_import_renewal_date'   },
  { key: 'fuel_grade_report', label: 'Fuel Grade Report',   priceField: 'fuel_grade_report_annual', renewalField: 'fuel_grade_report_renewal_date' },
  { key: 'inventory_control', label: 'Inventory Control',   priceField: 'inventory_control_annual', renewalField: 'inventory_control_renewal_date' }
];

function n(v) {
  const p = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(p) ? p : 0;
}
function moneyFmt(value) {
  if (value === '' || value == null) return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '';
  return `$${num.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Build the initial form state from the server's subscription row.
// Only the annual price matters for a lead — a non-blank price marks
// the module as part of the proposed package.
function fromServer(sub) {
  const out = {};
  for (const m of MODULES) {
    const price = sub?.[m.priceField];
    out[`${m.key}_price`] = price != null ? String(price) : '';
  }
  return out;
}

export default function LeadSubscriptionsTab({ leadId, subscription, onSaved }) {
  const [form, setForm] = useState(() => fromServer(subscription));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMessage, setSavedMessage] = useState(null);

  useEffect(() => {
    setForm(fromServer(subscription));
  }, [subscription]);

  // Subtotal sums every module with a price entered. This is the figure
  // that flows into Generate Quote for the lead.
  const subtotal = useMemo(() => {
    let total = 0;
    for (const m of MODULES) {
      const v = n(form[`${m.key}_price`]);
      if (v > 0) total += v;
    }
    return Math.round(total * 100) / 100;
  }, [form]);

  function updatePrice(key) {
    return (e) => setForm((prev) => ({ ...prev, [`${key}_price`]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      // Build the server payload. Each module sends its annual price
      // (or null when blank — which excludes it from the package) and an
      // explicit null renewal_date: leads carry no renewal dates, and
      // clearing it removes any stale value from before this change.
      const payload = {};
      for (const m of MODULES) {
        const price = form[`${m.key}_price`];
        payload[m.priceField]   = price !== '' ? Number(price) : null;
        payload[m.renewalField] = null;
      }
      await axios.put(`/api/subscriptions/${leadId}`, payload);
      setSavedMessage('Saved.');
      if (onSaved) onSaved();
      setTimeout(() => setSavedMessage(null), 2500);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="subscription-form" onSubmit={handleSubmit}>
      <p className="modal-sub" style={{ marginTop: 0 }}>
        Build the proposed package for this lead. Enter an annual price
        for each module they'd subscribe to — modules with a price are
        included, blank means not part of the package. The subtotal at
        the bottom feeds <strong>Generate Quote</strong> on this record.
      </p>

      <h4 className="preview-heading" style={{ marginTop: 'var(--space-3)' }}>
        Subscription Modules
      </h4>
      <div className="lead-sub-grid" role="table" aria-label="Subscription modules">
        <div className="lead-sub-grid-head" role="row">
          <div role="columnheader">Module</div>
          <div role="columnheader">Annual Price</div>
        </div>
        {MODULES.map((m) => {
          const badge = MODULE_BADGES[m.key];
          return (
            <div key={m.key} className="lead-sub-grid-row" role="row">
              <div className="lead-sub-label" role="cell">
                {badge && <Badge tone={badge.tone}>{badge.abbr}</Badge>}
                <span className="subscription-grid-label-text">{m.label}</span>
              </div>
              <div role="cell">
                <input
                  type="number" step="0.01"
                  className="cell-mono subscription-input subscription-input-price"
                  value={form[`${m.key}_price`]}
                  onChange={updatePrice(m.key)}
                  aria-label={`${m.label} annual price`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="lead-sub-subtotal" aria-label="Proposed annual subtotal">
        <span className="lead-sub-subtotal-label">Annual Subtotal</span>
        <span className="lead-sub-subtotal-value cell-mono">{moneyFmt(subtotal)}</span>
      </div>

      {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}
      {savedMessage && <p className="save-message">{savedMessage}</p>}

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Subscription'}
        </button>
      </div>
    </form>
  );
}
